// src/hooks/useImageProcessing.js
import { useState } from 'react';
import { createWorker } from 'tesseract.js';

export const useImageProcessing = (AZURE_ENDPOINT, AZURE_API_KEY) => {
  const [isProcessing, setIsProcessing] = useState(false);

  // Helper function to add delay between retries
  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  // Process image with Azure Computer Vision (with retries)
  const processWithAzure = async (file, retryCount = 0) => {
    const MAX_RETRIES = 3;
    const RETRY_DELAYS = [1000, 2000, 4000]; // 1s, 2s, 4s

    try {
      const base64Image = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file);
      });

      if (!AZURE_ENDPOINT || !AZURE_API_KEY) {
        throw new Error('Azure Endpoint ou API Key não configurados.');
      }

      const normalizedEndpoint = AZURE_ENDPOINT.endsWith('/') ? AZURE_ENDPOINT : `${AZURE_ENDPOINT}/`;
      const apiUrl = `${normalizedEndpoint}computervision/imageanalysis:analyze?api-version=2024-02-01&features=read&model-version=latest&language=en`;
      
      console.log('Requesting Azure URL:', apiUrl);

      const binaryString = window.atob(base64Image);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/octet-stream',
          'Ocp-Apim-Subscription-Key': AZURE_API_KEY
        },
        body: bytes
      });

      if (!response.ok) {
        const errorDetail = await response.text();
        console.error('Azure Error Response:', errorDetail);
        throw new Error(`Falha na análise Azure: ${response.status} ${response.statusText} - ${errorDetail}`);
      }

      const responseData = await response.json();
      
      if (responseData.readResult?.blocks?.[0]?.lines) {
        const lines = responseData.readResult.blocks[0].lines;
        const extractedText = lines.map(line => line.text).join('\n');
        console.log(`✅ Azure OCR sucesso no processamento`);
        return extractedText;
      }
      return '';
    } catch (error) {
      console.error(`❌ Tentativa ${retryCount + 1} do Azure falhou:`, error.message);
      
      // Retry logic with exponential backoff
      if (retryCount < MAX_RETRIES) {
        console.log(`🔄 Aguardando ${RETRY_DELAYS[retryCount]}ms antes de tentar novamente...`);
        await delay(RETRY_DELAYS[retryCount]);
        return processWithAzure(file, retryCount + 1);
      }
      
      // All retries exhausted
      throw error;
    }
  };

  // Fallback: Process image with Tesseract.js
  const processWithTesseract = async (file) => {
    console.log('⚠️ Iniciando fallback para Tesseract.js...');
    
    try {
      const worker = await createWorker('por', 1, {
        logger: (m) => {
          if (m.status === 'recognizing text') {
            console.log(`Tesseract progresso: ${Math.round(m.progress * 100)}%`);
          }
        }
      });

      const imageUrl = URL.createObjectURL(file);
      const { data: { text } } = await worker.recognize(imageUrl);
      
      await worker.terminate();
      URL.revokeObjectURL(imageUrl);
      
      console.log('✅ Tesseract.js processamento concluído');
      return text;
    } catch (error) {
      console.error('❌ Erro no Tesseract.js:', error);
      throw new Error('Falha tanto no Azure quanto no Tesseract.js');
    }
  };

  const processImage = async (file) => {
    setIsProcessing(true);
    try {
      // First: Try Azure with retries
      try {
        const azureResult = await processWithAzure(file);
        return azureResult;
      } catch (azureError) {
        console.warn('⚠️ Azure falhou após todas as tentativas. Usando Tesseract.js como fallback.');
        
        // Last resort: Use Tesseract.js
        const tesseractResult = await processWithTesseract(file);
        return tesseractResult;
      }
    } catch (error) {
      console.error('❌ Erro ao processar imagem com todos os métodos:', error);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  return { processImage, isProcessing };
};